import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrg } from '@/hooks/useCurrentOrg';

interface Course {
  id: string;
  name: string;
  description: string | null;
  grade: string | null;
  subject: string | null;
  is_published: boolean;
  created_at: string;
}

export default function OrgCourses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrg, canManage, loading: orgLoading } = useCurrentOrg();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', grade: '', subject: '' });

  const load = async () => {
    if (!currentOrg) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('courses')
      .select('id, name, description, grade, subject, is_published, created_at')
      .eq('org_id', currentOrg.org_id)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });
    if (error) toast.error('Failed to load courses');
    else setCourses(data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    if (orgLoading) return;
    if (!currentOrg) {
      setLoading(false);
      return;
    }
    load();
  }, [currentOrg, orgLoading]);

  const createCourse = async () => {
    if (!currentOrg || !user) return;
    if (!form.name.trim()) {
      toast.error('Course name is required');
      return;
    }
    setCreating(true);
    const { data, error } = await supabase
      .from('courses')
      .insert({
        org_id: currentOrg.org_id,
        created_by: user.id,
        name: form.name.trim(),
        description: form.description.trim() || null,
        grade: form.grade.trim() || null,
        subject: form.subject.trim() || null,
      })
      .select('id')
      .single();
    setCreating(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success('Course created');
    setOpen(false);
    setForm({ name: '', description: '', grade: '', subject: '' });
    navigate(`/org/courses/${data.id}`);
  };

  if (orgLoading || loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentOrg) {
    return (
      <div className="container max-w-3xl py-10">
        <Card>
          <CardHeader>
            <CardTitle>No organization</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Join or create an organization to manage courses.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl py-10">
      <Helmet>
        <title>Courses – {currentOrg.org_name}</title>
        <meta name="description" content="Manage courses and lessons for your organization." />
      </Helmet>

      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Courses</h1>
          <p className="text-sm text-muted-foreground">
            {currentOrg.org_name}
          </p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" /> New course
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create course</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="c-name">Name</Label>
                  <Input
                    id="c-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Grade 5 Math"
                  />
                </div>
                <div>
                  <Label htmlFor="c-desc">Description</Label>
                  <Textarea
                    id="c-desc"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="c-grade">Grade</Label>
                    <Input
                      id="c-grade"
                      value={form.grade}
                      onChange={(e) => setForm({ ...form, grade: e.target.value })}
                      placeholder="5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="c-subject">Subject</Label>
                    <Input
                      id="c-subject"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      placeholder="Math"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={createCourse} disabled={creating}>
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </header>

      {courses.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            <BookOpen className="mx-auto mb-3 h-8 w-8 opacity-40" />
            No courses yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {courses.map((c) => (
            <Link
              key={c.id}
              to={`/org/courses/${c.id}`}
              className="block rounded-lg border bg-card p-4 hover:bg-accent transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  {c.description && (
                    <div className="text-sm text-muted-foreground truncate">
                      {c.description}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {c.grade && <Badge variant="secondary">Grade {c.grade}</Badge>}
                    {c.subject && <Badge variant="secondary">{c.subject}</Badge>}
                  </div>
                </div>
                <Badge variant={c.is_published ? 'default' : 'outline'}>
                  {c.is_published ? 'Published' : 'Draft'}
                </Badge>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
